import { FC, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { IconButton, Stack, SvgIcon } from '@mui/material';
import { ThemeProvider } from '@mui/material/styles';
import { getAuth, User } from 'firebase/auth';

import { useAppLaunched, useBusinessLayer } from '@mopla/business-logic';
import { ErrorResponseDto, IWhoami } from '@mopla/data-models';
import { passengerThemeV2 } from '@mopla/mui';
import { BottomSlider, CrossIcon, MplLoader, MplPageHeader } from '@mopla/ui';
import LeftChevronIcon from '@mopla/ui/icons/ChevronLeft.svg';
import { BottomSliderScreenWrapper } from '@mopla/ui/layouts/WelcomeScreen';

import { withAuthGuard } from '../../routes/withAuthGuard';

import { ChangePasswordFlow } from './ui/ChangePasswordFlow/ChangePasswordFlow';
import { EmailPasswordSignInFlow } from './ui/EmailPasswordSignInFlow/EmailPasswordSignInFlow';
import { ForgotPasswordFlow } from './ui/ForgotPasswordFlow/ForgotPasswordFlow';
import { PersonalDetailsFlow } from './ui/PersonalDetailsFlow/PersonalDetailsFlow';
import { RegistrationFlow } from './ui/RegistrationFlow/RegistrationFlow';
import { TryOurApp } from './ui/TryOurApp/TryOurApp';
import { VerifyEmailFailedFlow } from './ui/VerifyEmailFailedFlow/VerifyEmailFailedFlow';
import { VerifyEmailFlow } from './ui/VerifyEmailFlow/VerifyEmailFlow';
import { WithPasswordless } from './ui/WithPasswordless';
import { useInterceptor } from './utils/useInterceptor';
import { VerificationStatus, WelcomeStep } from './constants';
import { changeEmail, verifyEmail } from './helpers';
import { BackButton } from './styled';
import {
	useWelcomePageStoreContext,
	WelcomePageProvider,
} from './useWelcomePageStore';
import { WelcomeEntry } from './WelcomeEntry';

export const WelcomeScreenV2Component: FC = () => {
	const { t } = useTranslation([
		'logIn',
		'registration',
		'welcome',
		'verify',
		'pushToApp',
	]);
	const navigate = useNavigate();
	const location = useLocation();
	const [query] = useSearchParams();
	const { api } = useBusinessLayer();

	const {
		setStepFromLocation,
		setSuccessType,
		activeStep,
		setStep,
		dispatch,
		isLoading,
		verificationStatus,
		isVerifyFailure,
	} = useWelcomePageStoreContext();

	const queryCode = query.get('code') as string;

	const { checking = true } = useAppLaunched();

	/**
	 * Any flow-component can set their own "back" or "close" interceptor.
	 * Whenever an interceptor is set, the corresponding button (close or back) is shown
	 * */
	const [backInterceptor, setBackInterceptor] = useInterceptor();
	const [closeInterceptor, setCloseInterceptor] = useInterceptor();

	useEffect(() => {
		if (!checking) {
			setStepFromLocation();

			if (!location.search.includes(WelcomeStep.verifyEmail)) {
				dispatch({ type: 'SET_IS_LOADING', payload: false });
			}
		}
	}, [setStepFromLocation, checking, location]);

	useEffect(() => {
		if (activeStep === WelcomeStep.verifyEmail) {
			dispatch({ type: 'SET_IS_LOADING', payload: true });

			getAuth().onAuthStateChanged((user) => {
				if (user?.emailVerified) {
					user
						.getIdTokenResult()
						.then(() => {
							return api.get('/api/whoami').then((res: IWhoami) => {
								if (res.pleaseComplete) {
									return navigate(
										{
											pathname: '/welcome',
											search: 'personalDetails',
										},
										{ state: { title: 'alreadyVerified' } }
									);
								} else {
									return navigate(
										{
											pathname: '/home',
										},
										{ state: { alreadyVerified: 'true' } }
									);
								}
							});
						})
						.catch((err: ErrorResponseDto) => console.log(err))
						.finally(() => {
							dispatch({ type: 'SET_IS_LOADING', payload: false });
						});
				} else {
					verifyEmail(
						location,
						navigate,
						user as User,
						t,
						verificationStatus,
						isLoading,
						dispatch
					);
				}
			});
		}
	}, [activeStep]);

	useEffect(() => {
		if (activeStep === WelcomeStep.verifyAndChangeEmail) {
			changeEmail(navigate, queryCode, t, location, dispatch);
		}
	}, [location, navigate, queryCode, t, setStep, activeStep]);

	const closeBottomSlider = useCallback(() => {
		setSuccessType(null);
		navigate({ pathname: '/welcome' });
	}, [navigate, setSuccessType]);

	const drawerOpened =
		(activeStep &&
			[
				WelcomeStep.login,
				WelcomeStep.registration,
				WelcomeStep.forgotPassword,
				WelcomeStep.resetPassword,
				WelcomeStep.personalDetails,
				WelcomeStep.tryOurApp,
				WelcomeStep.verifyBeforeStart,
			].includes(activeStep)) ||
		isVerifyFailure;

	const getStepPageHeader = () => {
		if (activeStep === WelcomeStep.tryOurApp) {
			return t('pushToApp:text.header');
		}

		return null;
	};

	const getBottomSliderContent = () => {
		if (
			isVerifyFailure &&
			verificationStatus !== VerificationStatus.rejectNoUserData
		) {
			return (
				<VerifyEmailFailedFlow
					setCloseInterceptor={setCloseInterceptor}
					onBottomSliderClose={closeBottomSlider}
				/>
			);
		}

		switch (activeStep) {
			case WelcomeStep.registration:
				return (
					<RegistrationFlow
						setBackInterceptor={setBackInterceptor}
						setCloseInterceptor={setCloseInterceptor}
						onBottomSliderClose={closeBottomSlider}
					/>
				);

			case WelcomeStep.forgotPassword:
				return (
					<ForgotPasswordFlow
						setBackInterceptor={setBackInterceptor}
						setCloseInterceptor={setCloseInterceptor}
						onBottomSliderClose={closeBottomSlider}
					/>
				);

			case WelcomeStep.resetPassword:
				return (
					<ChangePasswordFlow
						setCloseInterceptor={setCloseInterceptor}
						onBottomSliderClose={closeBottomSlider}
					/>
				);

			/** TODO MC-8971 refactor and move this render under the PrivateRoute component */
			case WelcomeStep.personalDetails:
				return withAuthGuard(<PersonalDetailsFlow />, {
					requireUser: true,
					redirectTo: '/welcome',
				});

			case WelcomeStep.verifyBeforeStart:
				return (
					<VerifyEmailFlow
						setCloseInterceptor={setCloseInterceptor}
						onBottomSliderClose={closeBottomSlider}
					/>
				);

			case WelcomeStep.tryOurApp:
				return withAuthGuard(<TryOurApp />, {
					requireUser: true,
					redirectTo: '/welcome',
				});

			default:
				return (
					<EmailPasswordSignInFlow
						setBackInterceptor={setBackInterceptor}
						setCloseInterceptor={setCloseInterceptor}
						onBottomSliderClose={closeBottomSlider}
					/>
				);
		}
	};

	const handleBottomSliderBackClick = useCallback(() => {
		if (backInterceptor) {
			backInterceptor();
		}
	}, [backInterceptor]);

	const handleBottomSliderCloseClick = useCallback(() => {
		if (closeInterceptor) {
			closeInterceptor();
		}
	}, [closeInterceptor]);

	const pageTitle = getStepPageHeader();
	const bottomSliderContent = getBottomSliderContent();

	return (
		<ThemeProvider theme={passengerThemeV2}>
			<WelcomeEntry
				onEmailPasswordLoginClick={() => {
					navigate({ pathname: '/welcome', search: 'login' });
				}}
				onSignUpClick={() => {
					navigate({ pathname: '/welcome', search: 'registration' });
				}}
			/>
			<BottomSlider open={drawerOpened} withHeader={false}>
				<BottomSliderScreenWrapper>
					{pageTitle && (
						<MplPageHeader
							className="legacyTitle"
							open
							title={pageTitle}
							showClose={false}
						/>
					)}

					{(backInterceptor || closeInterceptor) && (
						<Stack
							flexDirection="row"
							alignItems="center"
							className="topControls"
						>
							{backInterceptor && (
								<BackButton
									variant="text"
									startIcon={<SvgIcon component={LeftChevronIcon} />}
									onClick={handleBottomSliderBackClick}
									disableRipple
								>
									{t('welcome:button.back')}
								</BackButton>
							)}
							{closeInterceptor && (
								<IconButton
									sx={{ ml: 'auto' }}
									onClick={handleBottomSliderCloseClick}
								>
									<SvgIcon component={CrossIcon} />
								</IconButton>
							)}
						</Stack>
					)}
					{bottomSliderContent}
				</BottomSliderScreenWrapper>
			</BottomSlider>
			{/*<WelcomeBackground />*/}
			<MplLoader isLoading={isLoading || checking} />
		</ThemeProvider>
	);
};
//TODO resend email seems to be not working
export const WelcomeScreenV2 = () => {
	return (
		<WelcomePageProvider>
			<WithPasswordless>
				<WelcomeScreenV2Component />
			</WithPasswordless>
		</WelcomePageProvider>
	);
};
